import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { apiClient as api } from '../../api';

interface Question {
  id?: number;
  question_key: string;
  question_text: string;
  answer_type: string;
  answer_options?: string[];
  is_required: boolean;
}

interface Rule {
  rule_name: string;
  action_type: string;
  target_risk_ids: number[];
}

interface TestStepProps {
  tenantId?: number;
  questions: Question[];
  rules: Rule[];
  onNext: (results: any) => void;
  onBack: () => void;
}

const TestStep: React.FC<TestStepProps> = ({
  tenantId,
  questions,
  rules,
  onNext,
  onBack
}) => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatistics();
  }, [rules]);

  const loadStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all risk levels to calculate statistics
      const [level2Response, level3Response, level4Response] = await Promise.all([
        api.get('/api/risk-scoping/level2-risks'),
        api.get('/api/risk-scoping/level3-risks'),
        api.get('/api/risk-scoping/level4-risks')
      ]);

      const totalL2 = level2Response.data.length;
      const totalL3 = level3Response.data.length;
      const totalL4 = level4Response.data.length;
      const totalRisks = totalL2 + totalL3 + totalL4;

      // Calculate excluded risks based on rules
      const excludedRiskIds = new Set<number>();
      const flaggedRiskIds = new Set<number>();
      const includedRiskIds = new Set<number>();

      rules.forEach(rule => {
        if (rule.action_type === 'EXCLUDE_RISKS') {
          rule.target_risk_ids?.forEach(id => excludedRiskIds.add(id));
        } else if (rule.action_type === 'FLAG_FOR_REVIEW') {
          rule.target_risk_ids?.forEach(id => flaggedRiskIds.add(id));
        } else if (rule.action_type === 'INCLUDE_RISKS') {
          rule.target_risk_ids?.forEach(id => includedRiskIds.add(id));
        }
      });

      const excludedCount = excludedRiskIds.size;
      const flaggedCount = flaggedRiskIds.size;
      const remainingCount = totalRisks - excludedCount;
      const reductionPercentage = totalRisks > 0 ? Math.round((excludedCount / totalRisks) * 100) : 0;

      setStatistics({
        totalRisks,
        totalL2,
        totalL3,
        totalL4,
        excludedRisks: excludedCount,
        flaggedRisks: flaggedCount,
        applicableRisks: remainingCount,
        reductionPercentage,
        rulesCount: rules.length
      });
    } catch (err: any) {
      console.error('Failed to load statistics:', err);
      setError('Failed to load risk statistics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    onNext(statistics);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Calculating risk statistics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onBack}>
            ← Back
          </Button>
          <Button variant="contained" onClick={loadStatistics}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Risk Scoping Configuration Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review the impact of your configured rules on the risk taxonomy.
        </Typography>
      </Box>

      {/* Main Statistics Card */}
      <Card elevation={3} sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h3" color="primary" fontWeight="bold" gutterBottom>
              {statistics?.totalRisks || 0}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Total Risks in Taxonomy
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({statistics?.totalL2 || 0} L2 + {statistics?.totalL3 || 0} L3 + {statistics?.totalL4 || 0} L4 risks)
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="600" color="error">
                Excluded Risks
              </Typography>
              <Typography variant="body2" fontWeight="600" color="error">
                {statistics?.excludedRisks || 0} ({statistics?.reductionPercentage || 0}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={statistics?.reductionPercentage || 0}
              color="error"
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="600" color="success.main">
                Remaining Risks (Will be used to evaluate processes)
              </Typography>
              <Typography variant="body2" fontWeight="600" color="success.main">
                {statistics?.applicableRisks || 0} ({100 - (statistics?.reductionPercentage || 0)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={100 - (statistics?.reductionPercentage || 0)}
              color="success"
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {statistics?.flaggedRisks > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" fontWeight="600" color="warning.main">
                  Flagged for Review
                </Typography>
                <Typography variant="body2" fontWeight="600" color="warning.main">
                  {statistics?.flaggedRisks || 0}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(statistics?.flaggedRisks / statistics?.totalRisks) * 100}
                color="warning"
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Summary Text */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Summary:</strong> Of the potential <strong>{statistics?.totalRisks}</strong> risks in your taxonomy,{' '}
          <strong style={{ color: '#d32f2f' }}>{statistics?.excludedRisks} ({statistics?.reductionPercentage}%)</strong> have been excluded
          by your configured rules. The remaining <strong style={{ color: '#388e3c' }}>{statistics?.applicableRisks}</strong> risks
          will be used to evaluate processes in your organization.
        </Typography>
      </Alert>

      {/* Rules Summary */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
            📋 Configuration Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Questions Configured
              </Typography>
              <Typography variant="h6" color="primary">
                {questions.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Rules Configured
              </Typography>
              <Typography variant="h6" color="primary">
                {statistics?.rulesCount || 0}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="success" icon={<CheckIcon />}>
        <Typography variant="body2">
          Your risk scoping configuration is ready! Click <strong>Save Configuration</strong> to apply these settings.
        </Typography>
      </Alert>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack}>
          ← Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          size="large"
        >
          Save Configuration →
        </Button>
      </Box>
    </Box>
  );
};

export default TestStep;
